import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal, X, ChevronRight, Search } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import { breadcrumbLd, collectionPageLd, SITE_URL } from "@/lib/jsonld";
import ProductCard from "@/components/common/ProductCard";
import QuickView from "@/components/common/QuickView";
import { stagger } from "@/lib/motion";

const PRICE_RANGES = [
  { label: "All prices", value: "all" },
  { label: "Under ₹300", value: "0-300" },
  { label: "₹300 - ₹500", value: "300-500" },
  { label: "₹500 - ₹700", value: "500-700" },
  { label: "Above ₹700", value: "700-99999" },
];
const SORTS = [
  { label: "Featured", value: "featured" },
  { label: "Price: Low to High", value: "price_asc" },
  { label: "Price: High to Low", value: "price_desc" },
  { label: "Top Rated", value: "rating" },
  { label: "Most Popular", value: "popular" },
];
const POPULAR_SEARCHES = [
  "Car Shampoo", "Scratch", "Rust", "Ceramic Wax",
  "Tyre Shine", "Interior Cleaner", "Microfiber", "AC Cleaner",
];

function Filters({ categories, types, category, setCategory, type, setType, price, setPrice, availability, setAvailability }) {
  return (
    <div className="space-y-7">
      <div>
        <p className="mb-3 text-[12px] uppercase tracking-[0.16em] text-white/50">Category</p>
        <div className="flex flex-col gap-1.5">
          {[{ slug: "all", name: "All Products" }, ...categories].map((c) => (
            <button
              key={c.slug}
              data-testid={`shop-filter-category-${c.slug}`}
              onClick={() => setCategory(c.slug)}
              className={`flex items-center justify-between rounded-[10px] px-3 py-2 text-left text-[14px] transition-colors ${
                category === c.slug ? "bg-white text-black" : "text-white/70 hover:bg-white/5"
              }`}
            >
              {c.name}
              <ChevronRight size={14} className={category === c.slug ? "text-black/60" : "text-white/30"} />
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-3 text-[12px] uppercase tracking-[0.16em] text-white/50">Price</p>
        <div className="flex flex-col gap-1.5">
          {PRICE_RANGES.map((p) => (
            <button
              key={p.value}
              onClick={() => setPrice(p.value)}
              className={`rounded-[10px] px-3 py-2 text-left text-[14px] transition-colors ${
                price === p.value ? "bg-white/10 text-white" : "text-white/65 hover:bg-white/5"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-3 text-[12px] uppercase tracking-[0.16em] text-white/50">Product Type</p>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="shop-filter-type-select" className="border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent className="max-h-72 border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white">
            <SelectItem value="all">All types</SelectItem>
            {types.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <p className="mb-3 text-[12px] uppercase tracking-[0.16em] text-white/50">Availability</p>
        <label className="flex cursor-pointer items-center gap-2.5 text-[14px] text-white/70">
          <input
            type="checkbox"
            data-testid="shop-filter-availability-checkbox"
            checked={availability === "in_stock"}
            onChange={(e) => setAvailability(e.target.checked ? "in_stock" : "all")}
            className="h-4 w-4 accent-[#E11B22]"
          />
          In stock only
        </label>
      </div>
    </div>
  );
}

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [quickView, setQuickView] = useState(null);
  const [mobileFilters, setMobileFilters] = useState(false);

  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const [type, setType] = useState("all");
  const [price, setPrice] = useState("all");
  const [availability, setAvailability] = useState("all");
  const [sort, setSort] = useState("featured");
  const search = searchParams.get("search") || "";

  useEffect(() => {
    api.getCategories().then(setCategories).catch(() => {});
    api.getProductTypes().then(setTypes).catch(() => {});
  }, []);

  useEffect(() => {
    const urlCat = searchParams.get("category");
    if (urlCat && urlCat !== category) setCategory(urlCat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    const [min, max] = price === "all" ? [undefined, undefined] : price.split("-").map(Number);
    api
      .getProducts({
        category,
        type,
        availability,
        sort,
        search: search || undefined,
        min_price: min,
        max_price: max,
        limit: 60,
      })
      .then((d) => {
        setProducts(d.items);
        setTotal(d.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, type, price, availability, sort, search]);

  const updateCategory = (slug) => {
    setCategory(slug);
    const next = new URLSearchParams(searchParams);
    if (slug === "all") next.delete("category");
    else next.set("category", slug);
    setSearchParams(next, { replace: true });
  };

  const activeCat = useMemo(() => categories.find((c) => c.slug === category), [categories, category]);
  const filterProps = { categories, types, category, setCategory: updateCategory, type, setType, price, setPrice, availability, setAvailability };

  // Reset filters and optionally apply a search term or category (used by no-results state).
  const applySearch = (term, cat) => {
    setCategory(cat || "all");
    setType("all");
    setPrice("all");
    setAvailability("all");
    setSort("featured");
    const next = {};
    if (term) next.search = term;
    if (cat) next.category = cat;
    setSearchParams(next);
  };

  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo
        title="Shop"
        description="Browse premium AutoCareX car care products — shampoos, polishes, aerosols, accessories and more."
        jsonLd={[
          breadcrumbLd(
            activeCat
              ? [
                  { name: "Home", url: SITE_URL },
                  { name: activeCat.name, url: `${SITE_URL}/shop?category=${activeCat.slug}` },
                ]
              : [
                  { name: "Home", url: SITE_URL },
                  { name: "Shop", url: `${SITE_URL}/shop` },
                ]
          ),
          collectionPageLd({
            name: activeCat ? `${activeCat.name} — AutoCareX` : "AutoCareX Car Care Products",
            description: activeCat
              ? `Shop AutoCareX ${activeCat.name} products. ${activeCat.tagline || ""}`.trim()
              : "Browse premium AutoCareX car care products — shampoos, polishes, aerosols, accessories and more.",
            url: `${SITE_URL}/shop`,
            products,
          }),
        ]}
      />

      {/* Header band */}
      <div className="border-b border-[color:var(--acx-border)] bg-[var(--acx-bg-2)]">
        <div className="acx-container px-4 py-10 sm:px-6 lg:px-10 lg:py-14">
          <nav data-testid="shop-breadcrumbs" className="flex items-center gap-2 text-[13px] text-white/50">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link to="/shop" className="hover:text-white">Shop</Link>
            {activeCat && (<><span>/</span><span className="text-white/80">{activeCat.name}</span></>)}
          </nav>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl">
            {search ? `Search: “${search}”` : activeCat ? activeCat.label : "All Products"}
          </h1>
          <p className="mt-2 text-[15px] text-white/55">{total} products available</p>
        </div>
      </div>

      <div className="acx-container px-4 py-10 sm:px-6 lg:px-10">
        <div className="flex flex-col gap-8 lg:flex-row">
          {/* Desktop filters */}
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-28 rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-5">
              <Filters {...filterProps} />
            </div>
          </aside>

          <div className="flex-1">
            {/* Toolbar */}
            <div className="mb-6 flex items-center justify-between gap-3">
              <Sheet open={mobileFilters} onOpenChange={setMobileFilters}>
                <SheetTrigger asChild>
                  <button data-testid="shop-open-filters-button" className="inline-flex items-center gap-2 rounded-[12px] border border-[color:var(--acx-border)] bg-white/5 px-4 py-2.5 text-[14px] text-white lg:hidden">
                    <SlidersHorizontal size={16} /> Filters
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[300px] overflow-y-auto border-r border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] text-white">
                  <SheetHeader className="text-left"><SheetTitle className="text-white">Filters</SheetTitle></SheetHeader>
                  <div className="mt-6"><Filters {...filterProps} /></div>
                </SheetContent>
              </Sheet>

              <div className="ml-auto flex items-center gap-2">
                <span className="hidden text-[13px] text-white/50 sm:inline">Sort by</span>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger data-testid="shop-sort-select" className="w-[180px] border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white">
                    {SORTS.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active chips */}
            {(category !== "all" || type !== "all" || price !== "all" || availability !== "all" || search) && (
              <div className="mb-5 flex flex-wrap items-center gap-2">
                {search && <span className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--acx-border)] bg-white/5 px-3 py-1 text-[12px] text-white/70">“{search}”</span>}
                {category !== "all" && <Chip label={activeCat?.name || category} onClear={() => updateCategory("all")} />}
                {type !== "all" && <Chip label={type} onClear={() => setType("all")} />}
                {price !== "all" && <Chip label={PRICE_RANGES.find((p) => p.value === price)?.label} onClear={() => setPrice("all")} />}
                {availability === "in_stock" && <Chip label="In stock" onClear={() => setAvailability("all")} />}
              </div>
            )}

            {/* Grid */}
            {loading ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-[420px] animate-pulse rounded-[14px] border border-[color:var(--acx-border)] bg-[var(--acx-card)]" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] px-6 py-20 text-center" data-testid="shop-no-results">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[color:var(--acx-border)] bg-white/5 text-white/60">
                  <Search size={24} />
                </span>
                <h3 className="mt-5 font-display text-2xl font-bold tracking-[-0.01em]">
                  {search ? `No results for “${search}”` : "No products match your filters"}
                </h3>
                <p className="mt-2 max-w-md text-[14px] text-white/55">
                  {search
                    ? "We couldn't find a match. Try a different term, or explore these popular searches and categories."
                    : "Try adjusting or clearing your filters to see more products."}
                </p>

                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {POPULAR_SEARCHES.map((term) => (
                    <button
                      key={term}
                      onClick={() => applySearch(term)}
                      className="rounded-full border border-[color:var(--acx-border)] bg-white/5 px-3 py-1.5 text-[13px] text-white/75 transition-colors hover:bg-white/10 hover:text-white"
                    >
                      {term}
                    </button>
                  ))}
                </div>

                {categories.length > 0 && (
                  <div className="mt-6 flex flex-wrap justify-center gap-2">
                    {categories.map((c) => (
                      <button
                        key={c.slug}
                        onClick={() => applySearch(null, c.slug)}
                        className="rounded-[10px] border border-[color:var(--acx-border)] bg-white/5 px-3.5 py-2 text-[13px] font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => applySearch(null)}
                  className="mt-7 rounded-[12px] bg-white px-5 py-2.5 text-[14px] font-semibold text-black transition-colors hover:bg-white/90"
                >
                  Browse all products
                </button>
              </div>
            ) : (
              <motion.div initial="hidden" animate="show" variants={stagger} className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="shop-product-grid">
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} onQuickView={setQuickView} />
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </div>

      <QuickView product={quickView} open={!!quickView} onOpenChange={(o) => !o && setQuickView(null)} />
    </div>
  );
}

function Chip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--acx-border)] bg-white/5 px-3 py-1 text-[12px] text-white/80">
      {label}
      <button onClick={onClear} aria-label="Clear filter" className="text-white/50 hover:text-white"><X size={12} /></button>
    </span>
  );
}
